import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ReactTyped } from 'react-typed';
import { transition1 } from '../transitions';

import berlin from '../assets/img/helloBerlin.png';
import missionImg from '../assets/img/unsere mission.png';
import visionImg from '../assets/img/vision2.png';

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { ...transition1, delay } },
});

const UnsereMission = () => {
  useEffect(() => {
    document.title = 'Unsere Mission';
  }, []);

  return (
    <motion.div
      className="min-h-screen bg-gray-50 overflow-x-hidden"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      transition={transition1}
    >
      <div className="container mx-auto px-4 mt-28 sm:px-6 lg:px-8 py-12 sm:py-16 text-[#103d5b]" lang="de">

        {/* ===== عنوان الصفحة ===== */}
        <motion.div
          variants={fadeUp(0.1)}
          initial="hidden"
          animate="show"
          className="text-center mb-10 sm:mb-14"
        >
          <h1 className="font-extrabold tracking-tight leading-tight break-words text-3xl sm:text-4xl md:text-5xl text-[#103d5b]">
            Unsere{' '}
            <span className="text-[#7da893]">
              <ReactTyped
                strings={['Mission', 'Vision']}
                typeSpeed={120}
                backSpeed={70}
                loop
                backDelay={1500}
                showCursor={true}
                cursorChar="|"
              />
            </span>
          </h1>

          <p className="mt-4 text-base sm:text-lg md:text-xl text-[#103d5b] max-w-3xl mx-auto leading-relaxed">
            Wir bauen nicht nur Gebäude – wir gestalten Lebensräume, in denen Menschen leben, arbeiten und sich wohlfühlen.
            <br />
            Die YOUKA GmbH steht für Qualität, Zuverlässigkeit und Handwerkskunst im Bauwesen.
            <br />
            Durch die Verbindung von traditionellem Handwerk und moderner Technologie schaffen wir langlebige, energieeffiziente und nachhaltige Lösungen.
          </p>
        </motion.div>

        {/* ===== قسم Mission ===== */}
        <motion.div
          variants={fadeUp(0.2)}
          initial="hidden"
          animate="show"
          className="grid md:grid-cols-2 gap-12 items-center mb-20"
        >
          {/* الصورة يسار - النص يمين */}
          <div className="flex justify-center">
           <img
  src={missionImg}
  alt="Unsere Mission"
  className="rounded-2xl shadow-2xl object-contain border border-gray-200 w-full max-w-xl h-[400px] bg-white p-2"
/>


          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#103d5b] mb-4">Unsere Mission</h2>
            <p className="text-base sm:text-lg leading-relaxed text-[#103d5b]">
              Unsere Mission ist es, durch präzise Handwerkskunst, zuverlässiges Projektmanagement und transparente Kommunikation
              die Erwartungen unserer Kunden bei jedem Bau- und Renovierungsprojekt zu übertreffen. Wir schaffen nachhaltige und
              qualitativ hochwertige Ergebnisse, die Bestand haben.
            </p>
          </div>
        </motion.div>

        {/* ===== قسم Vision ===== */}
        <motion.div
          variants={fadeUp(0.3)}
          initial="hidden"
          animate="show"
          className="grid md:grid-cols-2 gap-12 items-center mb-20"
        >
          {/* النص يسار - الصورة يمين */}
          <div className="order-2 md:order-1">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#103d5b] mb-4">Unsere Vision</h2>
            <p className="text-base sm:text-lg leading-relaxed text-[#103d5b]">
              Wir streben danach, der führende Anbieter für innovative und digital unterstützte Baulösungen in Berlin zu werden.
              Durch den Einsatz moderner Technologien möchten wir den Bauprozess für Kunden und Partner einfacher, transparenter
              und effizienter gestalten.
            </p>
          </div>

          <div className="flex justify-center order-1 md:order-2">
             <img
              src={visionImg}
              alt="Unsere Vision"
              className="rounded-2xl shadow-2xl object-cover border border-gray-200 w-full max-w-xl h-[350px] sm:h-[420px]"
            />
          </div>
        </motion.div>

        {/* ===== الصورة الزجاجية ===== */}
        <motion.div
          variants={fadeUp(0.35)}
          initial="hidden"
          animate="show"
          className="flex justify-center mb-20"
        >
          <div className="relative flex justify-center items-center mt-10">
            <div className="bg-white/30 backdrop-blur-md rounded-2xl p-4 shadow-lg border border-white/40">
              <img
                src={berlin}
                alt="hello berlin"
                className="rounded-2xl shadow-2xl max-w-4xl w-full object-cover border border-white/40"
              />
            </div>
          </div>
        </motion.div>

        {/* ===== القيم الأساسية ===== */}
        <motion.div
          variants={fadeUp(0.4)}
          initial="hidden"
          animate="show"
          className="bg-white rounded-xl shadow-lg p-6 sm:p-10"
        >
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-center text-[#103d5b] mb-8 md:mb-10">
            Unsere Kernwerte
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              { title: 'Qualität', desc: 'Wir verpflichten uns zu höchsten Standards bei Materialien und Ausführung.' },
              { title: 'Transparenz', desc: 'Offene und ehrliche Kommunikation in jeder Projektphase.' },
              { title: 'Innovation', desc: 'Einsatz digitaler Werkzeuge und moderner Bautechniken.' },
              { title: 'Nachhaltigkeit', desc: 'Wir bauen verantwortungsbewusst für eine bessere Zukunft.' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                variants={fadeUp(0.5 + i * 0.1)}
                initial="hidden"
                animate="show"
                className="text-center p-5 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <h4 className="text-lg sm:text-xl font-semibold mb-2">{item.title}</h4>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default UnsereMission;
